// Room + global state: condition evaluation + declarative effects. Pure (no deps) so it
// is trivially unit-testable and deterministic. The ONLY way to mutate state is applyEffect,
// driven by an interaction — never arbitrary code. Two stores: room-local state (resets on
// room entry) and global state (persists across rooms — saga/quest flags).
// See research/DECISIONS.md #6.
import type { Cond, Effect, StateVal } from "../core/schema";

export type RoomState = Record<string, StateVal>;

/** The carried flame lives in global state under this reserved key (a number). */
export const FLAME = "flame";
export const flameOf = (global: RoomState): number => Number(global[FLAME]) || 0;

/** A condition holds when (if it has a key) store[key] === eq (or, eq omitted, is truthy)
 * AND (if it has minFlame) the carried flame is at least that bright. Store chosen by
 * cond.scope ("room" default, or "global"). */
export function holds(cond: Cond | undefined, room: RoomState, global: RoomState = {}): boolean {
  if (!cond) return true; // ungated
  if (cond.minFlame !== undefined && flameOf(global) < cond.minFlame) return false;
  if (cond.key !== undefined) {
    const store = cond.scope === "global" ? global : room;
    const v = store[cond.key];
    if (cond.eq === undefined) return !!v;
    return v === cond.eq;
  }
  return true; // flame-only condition, already satisfied above
}

/** Apply a declarative effect to the room and/or global stores IN PLACE. Returns the
 * changed keys (prefixed `global:` for global-store changes) — empty if nothing changed. */
export function applyEffect(effect: Effect | undefined, room: RoomState, global: RoomState = {}): string[] {
  const changed: string[] = [];
  if (!effect) return changed;
  const mutate = (store: RoomState, prefix: string, set?: RoomState, toggle?: string[]) => {
    if (set) for (const [k, v] of Object.entries(set)) if (store[k] !== v) (store[k] = v), changed.push(prefix + k);
    if (toggle) for (const k of toggle) ((store[k] = !store[k]), changed.push(prefix + k));
  };
  mutate(room, "", effect.set, effect.toggle);
  mutate(global, "global:", effect.setGlobal, effect.toggleGlobal);
  if (effect.flame) {
    global[FLAME] = flameOf(global) + effect.flame; // kindling grows (or dims) the carried flame
    changed.push("global:" + FLAME);
  }
  return changed;
}
